// includes/js-src/janus-engine/pl/economy.ts — calculate_ability_economy_and_restrictions() twin (calc_pl_system.php:3038–3395).
// Every step is ported in the PHP order because the FINAL multiplier is the product of the steps in insertion order
// (float accumulation), then round(…, 3) and a 0.25 floor. Plan §6.5.
import type { ItemFieldRow } from '../compile/item-field-reader';
import { readItemNumericSlot } from '../compile/enchantment-template';
import { readItemUuidSlot, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { phpJsonTruthy } from '../compile/php-json-values';
import { phpRound, phpInt } from './php-round';
import { plCatalogs, plCostContract, checkUuid, type PlCostContract } from './contract';
import { containsTriggerKey } from './layer-cells';
import type { PlReadContext, Row } from './read-context';
import { isObj } from './read-context';

export interface EconomyResult { multiplier: number; steps: Record<string, Row> }

const nz = (v: unknown): unknown => v === undefined ? null : v;
/** PHP `count(layer_entries(...))`. */
export const entriesList = (v: unknown): unknown[] => Array.isArray(v) ? v : isObj(v) ? Object.values(v) : [];
/** The HELD inventory a root carries. An inventory INSTANCE's `inventory` bucket is its own row (inventory_id, template, holder, creator
 *  ids) — not a list of held items — so an instance holds nothing here; reading the row's values as entries priced its own template and
 *  holder as carried items (and a non-item id never resolved, so the claim's closure never converged). */
export const heldInventoryEntries = (root: { inventory?: unknown }): unknown[] =>
    isObj(root.inventory) && typeof (root.inventory as Record<string, unknown>).inventory_id === 'string' ? [] : entriesList(root.inventory);
/** ft_normalize_layer_entries_to_distinct_uuids() */
export function distinctEntryUuids(entries: unknown): string[] {
    const out: string[] = [];
    for (const e of entriesList(entries)) { const uid = readLayerListEntryUuid(e); if (uid !== '' && checkUuid(uid) && !out.includes(uid)) out.push(uid); }
    return out;
}

export function calculateAbilityEconomyAndRestrictions(ctx: PlReadContext, abilityArr: Row, abilityType: string, itemArr: Row, contract: PlCostContract = plCostContract): EconomyResult {
    const read = (name: string): unknown => nz(ctx.layerValue(abilityArr as ItemFieldRow, name));
    const has = (name: string): boolean => ctx.hasLayer(abilityArr as ItemFieldRow, name);
    const cats = plCatalogs(contract);
    if (!isObj(abilityArr) || Object.keys(abilityArr).length === 0) return { multiplier: 1.0, steps: {} }; // the PHP guard (display_warning + safe fallback)
    const steps: Record<string, Row> = {};

    // AP ECONOMY
    const apMult = ({ full_ability: 0.65, ability: 0.75, reaction_ability: 1.30, quick_ability: 1.15, opportunity_attack_ability: 1.20, free_ability: 1.45, passive_ability: 1.00, attack_ability: 1.15 } as Record<string, number>)[abilityType] ?? 1.00;
    steps.ap_economy = { type: abilityType, multiplier: apMult };

    // RANGE
    const rangeId = readItemUuidSlot(read('range') ?? '');
    const rangeVar = rangeId !== '' ? (cats.rangeTypes.getVariableName(rangeId) ?? 'self') : (abilityType === 'attack_ability' ? 'reach' : 'self');
    const rangeMult = ({ self: 0.80, reach: 0.95, close: 1.00, near: 1.10, far: 1.30, very_far: 1.60, unlimited: 2.50 } as Record<string, number>)[rangeVar] ?? 1.00;
    steps.range = { value: rangeVar, multiplier: rangeMult };

    // RECHARGE
    const begin = phpInt(readItemNumericSlot(read('recharge_range_begin') ?? 1, 1));
    const end = phpInt(readItemNumericSlot(read('recharge_range_end') ?? 6, 6));
    let rechargeMult: number;
    if (begin < 1 || end > 6 || begin > end) rechargeMult = 1.0;
    else {
        const prob = (end - begin + 1) / 6.0;
        rechargeMult = prob >= 1.0 ? 1.00 : prob >= 0.83 ? 0.70 : prob >= 0.67 ? 0.65 : prob >= 0.50 ? 0.55 : prob >= 0.33 ? 0.50 : 0.40;
        if (begin === 1 && prob < 1.0) rechargeMult = Math.min(1.0, rechargeMult * 1.08);
    }
    steps.recharge = { begin, end, probability: phpRound((end - begin + 1) / 6.0, 2), multiplier: phpRound(rechargeMult, 2), note: begin === 1 ? 'front-weighted (starts ready)' : 'rare recharge' };

    // LONG-REST RESET
    // once per combat (2026-09-29): several refills a day, so between an unlimited ability and a once-a-day one. It WINS over a
    // long-rest flag authored beside it, because the runtime refills at every combat boundary regardless (Codex review).
    // combat_reset is emitted only when authored, so every other ability's frozen breakdown keeps its exact shape.
    const eachCombat = phpJsonTruthy(read('ability_resets_each_combat'));
    steps.long_rest_reset = { multiplier: phpJsonTruthy(read('ability_resets_long_rest')) && !eachCombat ? 0.30 : 1.0 };
    if (eachCombat) steps.combat_reset = { multiplier: 0.50 };

    // WEAPON CLASSIFICATION RESTRICTION / draw-from-sheathed
    let wcMult = phpJsonTruthy(read('if_weapon_classification_equipped_active')) ? 0.60 : 1.0;
    if (phpJsonTruthy(read('draw_sheathed_weapon_for_attack_ability'))) wcMult = Math.min(wcMult, 0.60);
    steps.weapon_classification_restriction = { multiplier: wcMult, note: wcMult < 1 ? 'Must have specific weapon / draw sheathed' : 'None' };

    // TRIGGERS
    const hasTrigger = containsTriggerKey(abilityArr);
    const triggerMult = hasTrigger ? 0.75 : 1.0;
    steps.triggers = { count: hasTrigger ? 1 : 0, multiplier: phpRound(triggerMult, 2) };

    // ROLL TYPE
    let rollMult = 1.0;
    let rollVar: string | null = null;
    let rollUuid = readItemUuidSlot(read('roll_type') ?? '');
    if (rollUuid === '' && phpJsonTruthy(read('next_spell_target_rider')) && has('skill_roll_contestee')) rollUuid = String(cats.rollTypes.getId('contesting_roll') ?? '');
    if (rollUuid !== '') {
        const rollData = cats.rollTypes.getById(rollUuid);
        if (rollData) { rollMult = rollData.roll_type_pl_multiplier ?? 1.0; rollVar = rollData.variable_name ?? null; }
    }
    const contesterCount = entriesList(ctx.layerEntries(abilityArr as ItemFieldRow, 'skill_roll_contester')).length;
    const contesteeCount = entriesList(ctx.layerEntries(abilityArr as ItemFieldRow, 'skill_roll_contestee')).length;
    if (contesterCount > 1) rollMult *= 1.15;
    if (contesteeCount > 1) rollMult *= 0.85;
    steps.roll_type = { value: rollVar, roll_uuid: rollUuid !== '' ? rollUuid : null, contester_skills: contesterCount, contestee_skills: contesteeCount, multiplier: phpRound(rollMult, 2) };

    // SIZE RESTRICTION
    const sizeRestrict = phpInt(readItemNumericSlot(read('ability_only_works_against_size_category_number_below') ?? 0, 0));
    const sizeMult = sizeRestrict !== 0 ? Math.max(0.55, 1.0 - (sizeRestrict * 0.08)) : 1.0;
    steps.size_restriction = { value: sizeRestrict, multiplier: phpRound(sizeMult, 2), note: sizeRestrict !== 0 ? 'Size-limited' : 'No restriction' };

    // IGNORE RESISTANCE / DAMAGE MITIGATION
    const ignoreCount = (phpJsonTruthy(read('effect_ignores_damage_type_resistance')) ? 1 : 0) + (phpJsonTruthy(read('attacks_ignore_damage_mitigation')) ? 1 : 0);
    const ignoreMult = ignoreCount > 0 ? 1.0 + (ignoreCount * 0.22) : 1.0;
    steps.ignore_resistance = { count: ignoreCount, multiplier: phpRound(ignoreMult, 2), note: ignoreCount > 0 ? 'Ignores resistance and/or damage mitigation' : 'None' };

    // MAXIMUM DAMAGE AGAINST CREATURE TYPE
    const maxVsTypeCount = distinctEntryUuids(ctx.layerEntries(abilityArr as ItemFieldRow, 'maximum_damage_against_creature_type')).length;
    const maxVsTypeMult = maxVsTypeCount > 0 ? Math.min(1.15, 1.0 + (maxVsTypeCount * 0.05)) : 1.0;
    steps.maximum_damage_against_creature_type = { count: maxVsTypeCount, multiplier: phpRound(maxVsTypeMult, 2) };

    // AOE
    const aoeFt = phpInt(readItemNumericSlot(read('aoe_distance') ?? 0, 0));
    let aoeMult = 1.0; let aoeVar = ''; let aoeArea = 0; let aoeVol = 0;
    if (aoeFt > 0) {
        const aoeRaw = read('aoe_type') ?? '';
        const aoeId = readItemUuidSlot(typeof aoeRaw === 'string' ? aoeRaw : '');
        aoeVar = aoeId !== '' ? (cats.aoeTypes.getVariableName(aoeId) ?? '') : String(aoeRaw ?? '').toLowerCase();
        const depthFt = phpInt(readItemNumericSlot(read('aoe_distance_depth') ?? 5, 5));
        const heightFt = phpInt(readItemNumericSlot(read('aoe_distance_height') ?? 0, 0));
        switch (aoeVar) {
            case 'sphere': case 'cylinder': case 'circle': aoeArea = Math.PI * aoeFt * aoeFt; break;
            case 'cube': case 'square': aoeArea = aoeFt * aoeFt; break;
            case 'cone': aoeArea = aoeFt * aoeFt; break;
            case 'line': aoeArea = aoeFt * Math.max(5, heightFt); break;
            default: aoeArea = Math.PI * aoeFt * aoeFt;
        }
        switch (aoeVar) {
            case 'sphere': aoeVol = (4 / 3) * Math.PI * Math.pow(aoeFt, 3); break;
            case 'cube': aoeVol = Math.pow(aoeFt, 3); break;
            case 'cylinder': aoeVol = Math.PI * aoeFt * aoeFt * (heightFt > 0 ? heightFt : 10); break;
            case 'cone': aoeVol = (Math.PI / 3) * Math.pow(aoeFt, 3); break;
            case 'line': aoeVol = aoeFt * Math.max(5, heightFt) * Math.max(1, depthFt); break;
            default: aoeVol = 0;
        }
        aoeMult = 1.0 + (aoeArea / (aoeArea + 1257));
        const maxTargets = phpInt(readItemNumericSlot(read('max_targets') ?? 0, 0));
        if (maxTargets > 0) aoeMult = Math.min(aoeMult, 1.0 + 0.5 * Math.max(0, maxTargets - 1));
        if (phpJsonTruthy(read('persistent_aoe'))) aoeMult *= 1.5;
        if (phpJsonTruthy(read('aoe_follows_target'))) aoeMult *= 1.15;
        if (phpJsonTruthy(read('arcing_trajectory'))) aoeMult *= 1.10;
    }
    steps.aoe = { ft: aoeFt, shape: aoeVar, area_sqft: phpInt(phpRound(aoeArea, 0)), volume_cuft: phpInt(phpRound(aoeVol, 0)), multiplier: phpRound(aoeMult, 2) };

    // CHARGE GATING
    let chargeQty = phpInt(readItemNumericSlot(read('consumes_charges_qty') ?? 0, 0));
    if (has('consume_orb')) chargeQty = Math.max(1, chargeQty);
    const consumesCharges = has('consumes_ability_charges') || has('consume_orb') || chargeQty > 0;
    const chargeMult = consumesCharges ? Math.max(0.50, 0.70 - 0.05 * (Math.max(1, chargeQty) - 1)) : 1.0;
    steps.charge_gating = { multiplier: phpRound(chargeMult, 2), ...(consumesCharges && chargeQty > 1 ? { value: chargeQty + ' charges' } : {}) };

    // CONCENTRATION
    steps.concentration = { multiplier: phpJsonTruthy(read('spell_concentration')) ? 0.85 : 1.0 };

    // CASTING TIME
    const ctRaw = read('spell_casting_time');
    const ct = ctRaw === null ? '' : String(ctRaw);
    const ctMult = ({ '1ap': 1.15, '2ap': 0.75, '3ap': 0.65, reaction: 1.30, one_minute: 0.60, ten_minutes: 0.60, one_hour: 0.60, eight_hours: 0.60, twelve_hours: 0.60, twenty_four_hours: 0.60 } as Record<string, number>)[ct] ?? 1.00;
    steps.casting_time = { value: ct, multiplier: ctMult };

    // CONDITIONAL GATES
    const gateKeys = ['if_caster_has_status_effect_active', 'if_caster_has_status_effect_not_active', 'if_target_has_status_effect_active', 'if_target_has_status_effect_not_active',
        'if_weapon_category_equipped_active', 'if_weapon_type_equipped_active', 'if_armor_type_equipped_active', 'if_armor_type_equipped_not_active', 'if_light_type_active', 'if_light_type_not_active',
        'if_environment_active', 'if_environment_not_active', 'if_effective_light_type_active', 'if_effective_light_type_not_active', 'if_outdoors_active', 'if_outdoors_not_active', 'if_sunlight_active',
        'if_sunlight_not_active', 'if_caster_summon_within_ft', 'if_aura_owner_has_status_effect_active','if_weather_active', 'if_circle_of_magic_cast', 'if_weapon_has_property_equipped_active', 'if_weapon_has_property_equipped_not_active', 'requires_equipped_item',
        'triggers_on_caster_summon_death_creature_sub_type'];
    const gated = gateKeys.some(k => has(k));
    steps.conditional_gate = { multiplier: gated ? 0.85 : 1.0 };

    // TARGET-CLASS RESTRICTIONS
    let restrictMult = 1.0;
    for (const rk of ['ability_works_only_against_creature_type', 'ability_works_only_against_creature_sub_type', 'ability_works_only_against_creature_lineage', 'ability_works_only_against_creature_ancestry', 'ability_works_only_against_creature_grouping', 'ability_works_only_against_item_type', 'ability_works_only_on_weapon_classification']) {
        if (has(rk)) { restrictMult = 0.80; break; }
    }
    if (restrictMult === 1.0) {
        for (const rk of ['ability_does_not_work_against_creature_type', 'ability_does_not_work_against_creature_sub_type', 'ability_does_not_work_against_creature_lineage', 'ability_does_not_work_against_creature_ancestry', 'ability_does_not_work_against_creature_grouping', 'ability_does_not_work_on_weapon_category']) {
            if (has(rk)) { restrictMult = 0.92; break; }
        }
    }
    if (phpJsonTruthy(read('ability_only_works_against_size_category_number_above'))) restrictMult = Math.min(restrictMult, 0.85);
    if (phpJsonTruthy(read('ability_only_affects_inventory'))) restrictMult = Math.min(restrictMult, 0.50);
    steps.target_restriction = { multiplier: restrictMult };

    // TEAM FILTER
    const teamFilterApplies = phpJsonTruthy(read('effect_only_affects_opposing_team_type')) && (aoeFt > 0 || phpInt(readItemNumericSlot(read('max_targets') ?? 0, 0)) > 1);
    steps.team_filter = { multiplier: teamFilterApplies ? 1.10 : 1.0 };

    // OUT-OF-COMBAT GATE
    const ritualAlreadyPriced = (steps.casting_time!.multiplier as number) == 0.60;
    steps.combat_gate = { multiplier: (phpJsonTruthy(read('requires_out_of_combat')) && !ritualAlreadyPriced) ? 0.60 : 1.0 };

    steps.upcastable = { multiplier: phpJsonTruthy(read('spell_upcastable')) ? 1.08 : 1.0 };
    steps.contest_retry = { multiplier: phpJsonTruthy(read('target_retries_contesting_roll_on_end_of_turn')) ? 0.90 : 1.0 };
    // Psion Dominion of Thought (2026-10-07): the target also re-rolls whenever the caster's side damages it — a looser hold.
    steps.contest_retry_on_damage = { multiplier: phpJsonTruthy(read('target_retries_contesting_roll_on_damage_taken')) ? 0.95 : 1.0 };
    // Psion Override Emotion (2026-10-07): the caster picks ONE of the listed statuses — only one lands, though the pick is flexible.
    steps.status_caster_choice = { multiplier: phpJsonTruthy(read('status_effect_applied_is_caster_choice')) ? 0.75 : 1.0 };
    steps.contest_skip_same_team = { multiplier: phpJsonTruthy(read('contesting_roll_skipped_for_same_team_type')) ? 1.05 : 1.0 };

    // SAVE FOR HALF — the flag can live on the ability row or on the owning item.
    const saveHalf = has('contesting_roll_successful_for_half_damage') || ctx.hasLayer(itemArr as ItemFieldRow, 'contesting_roll_successful_for_half_damage');
    steps.save_for_half = { multiplier: saveHalf ? 1.20 : 1.0 };

    // FINAL MULTIPLIER
    let finalMult = 1.0;
    for (const step of Object.values(steps)) finalMult *= (typeof step.multiplier === 'number' ? step.multiplier : 1);
    finalMult = Math.max(0.25, phpRound(finalMult, 3));
    return { multiplier: finalMult, steps };
}
